import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import type { CelebrationIntensity } from '@/types/celebration'

interface ConfettiCelebrationProps {
  intensity?: CelebrationIntensity
  colors?: string[]
  duration?: number
  onComplete: () => void
}

interface Particle {
  id: number
  x: number
  y: number
  color: string
  size: number
  rotation: number
}

const PARTICLE_COUNTS: Record<CelebrationIntensity, number> = {
  subtle: 20,
  moderate: 40,
  burst: 80,
}

/**
 * Full-screen confetti celebration animation
 * Renders via portal for z-index isolation
 */
export function ConfettiCelebration({
  intensity = 'moderate',
  colors = ['bg-purple-400', 'bg-pink-400', 'bg-blue-400', 'bg-amber-300'],
  duration = 2000,
  onComplete,
}: ConfettiCelebrationProps) {
  const [isVisible, setIsVisible] = useState(true)

  // Generate particles with random positions
  const particles = useMemo<Particle[]>(() => {
    const count = PARTICLE_COUNTS[intensity]
    return Array.from({ length: count }, (_, i) => ({
      id: i,
      // Start from random horizontal position
      x: Math.random() * 100,
      // Start from top third of screen
      y: Math.random() * 30,
      color: colors[Math.floor(Math.random() * colors.length)],
      // Random sizes between 6px and 12px
      size: 6 + Math.random() * 6,
      // Random initial rotation
      rotation: Math.random() * 360,
    }))
  }, [intensity, colors])

  // Auto-dismiss after duration
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false)
    }, duration)

    return () => clearTimeout(timer)
  }, [duration])

  // Handle animation complete
  const handleExitComplete = () => {
    onComplete()
  }

  // Only render on client
  if (typeof window === 'undefined') {
    return null
  }

  return createPortal(
    <AnimatePresence onExitComplete={handleExitComplete}>
      {isVisible && (
        <motion.div
          className="fixed inset-0 z-[9999] pointer-events-none overflow-hidden"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          aria-hidden="true"
        >
          {particles.map((particle) => (
            <motion.div
              key={particle.id}
              className={`absolute rounded-full ${particle.color}`}
              style={{
                left: `${particle.x}%`,
                top: `${particle.y}%`,
                width: particle.size,
                height: particle.size,
              }}
              initial={{
                opacity: 0,
                scale: 0,
                rotate: particle.rotation,
              }}
              animate={{
                opacity: [0, 1, 1, 0],
                scale: [0, 1, 1, 0.5],
                rotate: particle.rotation + 360,
                x: (Math.random() - 0.5) * 300,
                y: [0, window.innerHeight * 0.8],
              }}
              transition={{
                duration: 1.5 + Math.random() * 0.5,
                delay: particle.id * 0.02,
                ease: [0.25, 0.46, 0.45, 0.94],
              }}
            />
          ))}
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  )
}
